import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Pagination } from '@/components/ui/pagination';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Input } from '@/components/ui/input';
import { Check, X, Plus, FileText, Search, ArrowUpDown, ArrowUp, ArrowDown, Eye, ChevronDown, ChevronRight, Trash2, Hand } from 'lucide-react';
import { toast } from 'sonner';
import { format } from 'date-fns';
import { getPhotoUrl } from '@/utils/photoUrl';
import { useAuth } from '@/contexts/AuthContext';
import NewManualOfferDialog from '@/components/ofertas/NewManualOfferDialog';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const badgeVariants = {
  Pendiente: 'default',
  Aceptada: 'outline',
  Rechazada: 'destructive',
};

const Ofertas = () => {
  const { user } = useAuth();
  const [ofertas, setOfertas] = useState([]);
  const [lotes, setLotes] = useState({});
  const [clientes, setClientes] = useState({});
  const [loading, setLoading] = useState(true);
  const [showNewOfferDialog, setShowNewOfferDialog] = useState(false);
  const [searchInput, setSearchInput] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [expandedLote, setExpandedLote] = useState(null);
  const [processingOffer, setProcessingOffer] = useState(null);

  // Pagination states
  const [currentPage, setCurrentPage] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [currentTab, setCurrentTab] = useState('nuevas');
  const [sortField, setSortField] = useState('hora');
  const [sortDirection, setSortDirection] = useState('desc');
  const pageSize = 10;
  const serverChunkSize = 100;

  useEffect(() => {
    loadDictionaries();
  }, []);

  // Debounce search input
  useEffect(() => {
    const delayDebounceFn = setTimeout(() => {
      setSearchTerm(searchInput);
    }, 600);
    return () => clearTimeout(delayDebounceFn);
  }, [searchInput]);

  useEffect(() => {
    setCurrentPage(1);
    loadOfertasChunk(0);
  }, [searchTerm, currentTab, sortField, sortDirection]);

  useEffect(() => {
    const requiredStartIndex = (currentPage - 1) * pageSize;
    if (requiredStartIndex >= ofertas.length && ofertas.length < totalCount) {
      loadOfertasChunk(ofertas.length);
    }
  }, [currentPage]);

  const loadDictionaries = async () => {
    try {
      const [lotesRes, clientesRes] = await Promise.all([
        axios.get(`${API}/lotes`),
        axios.get(`${API}/clientes?includeDeleted=true`),
      ]);
      const vehMap = {};
      lotesRes.data.forEach((v) => { vehMap[v.id] = v; });
      setLotes(vehMap);
      const revMap = {};
      clientesRes.data.forEach((r) => { revMap[r.id] = r; });
      setClientes(revMap);
    } catch (error) {
      console.error('Error loading dictionaries:', error);
    }
  };

  const loadOfertasChunk = async (skip) => {
    try {
      setLoading(true);
      const estadoFilter = currentTab === 'nuevas' ? 'Pendiente' : currentTab === 'aceptadas' ? 'Aceptada' : 'Rechazada';
      const res = await axios.get(`${API}/ofertas/paginated`, {
        params: {
          skip,
          limit: serverChunkSize,
          searchTerm: searchTerm || undefined,
          estadoFilter,
          sortField,
          sortDirection
        }
      });
      const { items, total } = res.data;
      setTotalCount(total);

      if (skip === 0) {
        setOfertas(items);
      } else {
        setOfertas(prev => {
          const newIds = items.map(i => i.id);
          const filteredPrev = prev.filter(p => !newIds.includes(p.id));
          return [...filteredPrev, ...items];
        });
      }
    } catch (error) {
      console.error('Error loading ofertas chunk:', error);
      toast.error('Error al cargar ofertas');
    } finally {
      setLoading(false);
    }
  };

  const refreshFullState = () => {
    loadDictionaries();
    loadOfertasChunk(0);
  };

  const handleAccept = async (ofertaId) => {
    if (processingOffer) return;
    setProcessingOffer(ofertaId);
    try {
      await axios.put(`${API}/ofertas/${ofertaId}`, { estado: 'Aceptada' });
      toast.success('Oferta aceptada');
      refreshFullState();
    } catch (error) {
      console.error('Error accepting offer:', error);
      toast.error('Error al aceptar oferta');
    } finally {
      setProcessingOffer(null);
    }
  };

  const handleReject = async (ofertaId) => {
    if (processingOffer) return;
    setProcessingOffer(ofertaId);
    try {
      await axios.put(`${API}/ofertas/${ofertaId}`, { estado: 'Rechazada' });
      toast.success('Oferta rechazada');
      refreshFullState();
    } catch (error) {
      console.error('Error rejecting offer:', error);
      toast.error('Error al rechazar oferta');
    } finally {
      setProcessingOffer(null);
    }
  };

  const handleDeleteOffer = async (ofertaId) => {
    if (!window.confirm("¿Está seguro de que desea eliminar permanentemente esta oferta?")) return;
    try {
      await axios.delete(`${API}/ofertas/${ofertaId}`);
      toast.success('Oferta eliminada');
      refreshFullState();
    } catch (error) {
      console.error('Error deleting offer:', error);
      toast.error(error.response?.data?.detail || 'Error al eliminar oferta');
    }
  };



  const groupOfertasByLote = (ofertasList) => {
    const grouped = {};
    ofertasList.forEach((oferta) => {
      if (!grouped[oferta.loteId]) {
        grouped[oferta.loteId] = [];
      }
      grouped[oferta.loteId].push(oferta);
    });
    return grouped;
  };

  const toggleLote = (loteId) => {
    if (expandedLote === loteId) {
      setExpandedLote(null);
    } else {
      setExpandedLote(loteId);
    }
  };

  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('desc');
    }
  };

  const SortableHeader = ({ field, children }) => {
    const isActive = sortField === field;
    return (
      <th className="text-left p-4 font-medium cursor-pointer hover:bg-accent/50" onClick={() => handleSort(field)}>
        <div className="flex items-center gap-2">
          {children}
          {isActive ? (
            sortDirection === 'asc' ? <ArrowUp className="h-4 w-4" /> : <ArrowDown className="h-4 w-4" />
          ) : (
            <ArrowUpDown className="h-4 w-4 opacity-30" />
          )}
        </div>
      </th>
    );
  };



  const currentDisplayOfertas = ofertas.slice((currentPage - 1) * pageSize, currentPage * pageSize);
  const groupedOfertas = groupOfertasByLote(currentDisplayOfertas);
  const loteIds = Object.keys(groupedOfertas);

  return (
    <div className="space-y-6 bg-background min-h-screen">
      {/* Header */}
      <div className="bg-background border-b">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div>
              <h1 className="text-3xl font-semibold tracking-tight">Ofertas</h1>
              <p className="text-muted-foreground mt-1">
                {totalCount} ofertas encontradas
              </p>
            </div>
            <Button onClick={() => setShowNewOfferDialog(true)}>
              <Plus className="h-4 w-4 mr-2" />
              Cargar Oferta Manual
            </Button>
          </div>
        </div>
      </div>

      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        {/* Búsqueda */}
        <Card className="p-4 mb-6">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Buscar por vehículo, cliente o empresa..."
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  setSearchTerm(searchInput);
                }
              }}
              className="pl-10"
              data-testid="search-offer-input"
            />
          </div>
        </Card>

        <Tabs value={currentTab} onValueChange={setCurrentTab} className="space-y-4">
          <TabsList>
            <TabsTrigger value="nuevas" data-testid="offers-tab-new">
              Nuevas {currentTab === 'nuevas' ? `(${totalCount})` : ''}
            </TabsTrigger>
            <TabsTrigger value="aceptadas">
              Aceptadas {currentTab === 'aceptadas' ? `(${totalCount})` : ''}
            </TabsTrigger>
            <TabsTrigger value="rechazadas">
              Rechazadas {currentTab === 'rechazadas' ? `(${totalCount})` : ''}
            </TabsTrigger>
          </TabsList>

          <TabsContent value={currentTab} className="space-y-4">
            {loading && ofertas.length === 0 ? (
              <Card className="p-12 text-center">
                <p className="text-muted-foreground">Cargando ofertas...</p>
              </Card>
            ) : totalCount === 0 ? (
              <Card className="p-12 text-center">
                <p className="text-muted-foreground">No hay ofertas</p>
              </Card>
            ) : (
              <Card>
                <div className="overflow-x-auto">
                  <table className="w-full min-w-[800px]">
                    <thead className="border-b">
                      <tr>
                        <SortableHeader field="lote"></SortableHeader>
                        <th className="text-left p-4 font-medium w-20">Foto</th>
                        <SortableHeader field="lote">Vehículo</SortableHeader>
                        <SortableHeader field="monto">Precio Base</SortableHeader>
                        <th className="text-left p-4 font-medium">Ofertas</th>
                        <th className="text-right p-4 font-medium">Acciones</th>
                      </tr>
                    </thead>
                    <tbody>
                      {loteIds.map((loteId) => {
                        const lote = lotes[loteId];
                        const loteOfertas = groupedOfertas[loteId];
                        const isExpanded = expandedLote === loteId;
                        const ofertasCount = loteOfertas.length;

                        return (
                          <React.Fragment key={loteId}>
                            {/* Fila del vehículo */}
                            <tr
                              className="border-b hover:bg-accent/50 cursor-pointer transition-colors duration-200"
                              onClick={() => toggleLote(loteId)}
                              data-testid="lote-row"
                            >
                              <td className="p-4">
                                <div className="transition-transform duration-300">
                                  {isExpanded ? (
                                    <ChevronDown className="h-5 w-5 text-muted-foreground transition-transform duration-300" />
                                  ) : (
                                    <ChevronRight className="h-5 w-5 text-muted-foreground transition-transform duration-300" />
                                  )}
                                </div>
                              </td>
                              <td className="p-4">
                                <div className="w-16 h-16 rounded-md overflow-hidden bg-muted">
                                  {lote?.fotos && lote.fotos[0] ? (
                                    <img src={getPhotoUrl(lote.fotos[0])} alt={lote.tipoGanado} className="w-full h-full object-cover" />
                                  ) : (
                                    <div className="w-full h-full flex items-center justify-center">
                                      <FileText className="h-6 w-6 text-muted-foreground" />
                                    </div>
                                  )}
                                </div>
                              </td>
                              <td className="p-4">
                                <div className="font-medium">
                                  {lote ? `${lote.tipoGanado} ${lote.categoria}` : 'N/A'}
                                </div>
                                <div className="text-sm text-muted-foreground">
                                  {lote?.raza} • {lote?.numeroLote}
                                </div>
                              </td>
                              <td className="p-4">
                                <div className="font-medium">
                                  {lote?.precioBase ? `$${lote.precioBase.toLocaleString('en-US')}` : 'Sin Base'}
                                </div>
                              </td>
                              <td className="p-4">
                                <Badge variant="secondary">
                                  {ofertasCount} {ofertasCount === 1 ? 'oferta' : 'ofertas'}
                                </Badge>
                              </td>
                              <td className="p-4">
                                <div className="flex justify-end gap-2">
                                  {lote && (
                                    <Button
                                      size="sm"
                                      variant="ghost"
                                      asChild
                                      onClick={(e) => e.stopPropagation()}
                                    >
                                      <Link to={`/publicaciones/${lote.id}`}>
                                        <Eye className="h-4 w-4" />
                                      </Link>
                                    </Button>
                                  )}
                                </div>
                              </td>
                            </tr>

                            {/* Filas de ofertas expandidas */}
                            {isExpanded && loteOfertas.map((oferta, index) => {
                              const cliente = clientes[oferta.clienteId];
                              return (
                                <tr
                                  key={oferta.id}
                                  className="border-b last:border-0 bg-accent/20 animate-in fade-in slide-in-from-top-2"
                                  style={{ animationDelay: `${index * 50}ms`, animationDuration: '300ms' }}
                                  data-testid="offers-row"
                                >
                                  <td className="p-4"></td>
                                  <td className="p-4"></td>
                                  <td className="p-4 pl-8">
                                    <div className="font-medium">
                                      {cliente?.nombre || 'N/A'}
                                      {cliente?.deletedAt && (
                                        <span className="ml-2 text-xs text-muted-foreground" title={`Eliminado el ${new Date(cliente.deletedAt).toLocaleDateString('es-UY')}`}>
                                          (eliminado)
                                        </span>
                                      )}
                                    </div>
                                    {cliente?.empresa && (
                                      <div className="text-sm text-muted-foreground">{cliente.empresa}</div>
                                    )}
                                  </td>
                                  <td className="p-4">
                                    <div className="text-lg font-bold text-primary">
                                      ${oferta.monto.toLocaleString('en-US')}
                                      {oferta.esManual && (
                                        <Badge variant="outline" className="ml-2 gap-1 text-[11px] h-5 border-primary text-primary">
                                          <Hand className="h-3 w-3" />
                                          Manual
                                        </Badge>
                                      )}
                                    </div>
                                  </td>
                                  <td className="p-4 text-sm text-muted-foreground">
                                    {format(new Date(oferta.hora), 'dd/MM/yyyy HH:mm')}
                                  </td>
                                  <td className="p-4">
                                    <div className="flex justify-end gap-2">
                                      {user?.rol === 'SuperAdmin' && (
                                        <Button
                                          size="icon"
                                          variant="ghost"
                                          className="h-8 w-8 text-destructive hover:bg-destructive/10 hover:text-destructive"
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            handleDeleteOffer(oferta.id);
                                          }}
                                          title="Eliminar oferta (SuperAdmin)"
                                        >
                                          <Trash2 className="h-4 w-4" />
                                        </Button>
                                      )}
                                      {oferta.estado === 'Pendiente' && (
                                        <>
                                          <Button
                                            size="sm"
                                            variant="default"
                                            disabled={!!processingOffer}
                                            onClick={(e) => {
                                              e.stopPropagation();
                                              handleAccept(oferta.id);
                                            }}
                                            data-testid="offers-accept-button"
                                          >
                                            {processingOffer === oferta.id ? (
                                              <div className="h-4 w-4 animate-spin rounded-full border-2 border-current border-t-transparent" />
                                            ) : (
                                              <Check className="h-4 w-4" />
                                            )}
                                          </Button>
                                          <Button
                                            size="sm"
                                            variant="outline"
                                            disabled={!!processingOffer}
                                            onClick={(e) => {
                                              e.stopPropagation();
                                              handleReject(oferta.id);
                                            }}
                                            data-testid="offers-reject-button"
                                          >
                                            <X className="h-4 w-4" />
                                          </Button>
                                        </>
                                      )}
                                    </div>
                                  </td>
                                </tr>
                              );
                            })}
                          </React.Fragment>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </Card>
            )}

            <Pagination
              currentPage={currentPage}
              totalPages={Math.ceil(totalCount / pageSize)}
              onPageChange={setCurrentPage}
            />
          </TabsContent>
        </Tabs>
      </div>

      {/* New Offer Dialog */}
      <NewManualOfferDialog
        open={showNewOfferDialog}
        onOpenChange={setShowNewOfferDialog}
        onSuccess={refreshFullState}
        lotes={lotes}
        clientes={clientes}
      />
    </div>
  );
};

export default Ofertas;